import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Grid,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import DownloadIcon from "@mui/icons-material/Download";
import Config from "../../Utils/Config";

interface ImageGalleryProps {
  images: string[];
  maxWidth?: number;
  maxHeight?: number;
  columns?: number;
}

/**
 * Converts image paths to proper URLs
 * Handles both CRM paths (serviceData/...) and ERP paths (../../CafeMag-Backend-CRM/assets/serviceData/...)
 */
const convertImagePath = (imagePath: string): string => {
  if (!imagePath) return "";

  // If it's already a full URL (starts with http:// or https://), return as is
  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
    return imagePath;
  }

  // Extract the filename from the path
  let cleanPath = imagePath;

  // Handle CRM-style paths: "serviceData/filename.jpg"
  if (imagePath.includes("serviceData/")) {
    const parts = imagePath.split("serviceData/");
    if (parts.length > 1) {
      cleanPath = `serviceData/${parts[parts.length - 1]}`;
    }
  }

  // Handle ERP-style paths: "../../CafeMag-Backend-CRM/assets/serviceData/filename.jpg"
  else if (imagePath.includes("CafeMag-Backend-CRM/assets/")) {
    const parts = imagePath.split("CafeMag-Backend-CRM/assets/");
    if (parts.length > 1) {
      cleanPath = parts[1];
    }
  }

  // Remove leading slashes and dots
  cleanPath = cleanPath.replace(/^[./]+/, "");

  // Use CRM API URL for images (they are stored in CRM backend)
  // CRM backend serves assets at /api/assets/
  return `${Config.crmApiUrl}/assets/${cleanPath}`;
};

export default function ImageGallery(props: ImageGalleryProps) {
  const {
    images,
    maxWidth = 150,
    maxHeight = 150,
    columns = 3,
  } = props;

  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Filter only image files and convert paths to proper URLs
  const imageFiles = images
    .filter((doc) => /\.(jpe?g|png|gif|webp|svg)$/i.test(doc))
    .map((doc) => convertImagePath(doc));

  if (imageFiles.length === 0) {
    return null;
  }

  const handleOpen = (index: number) => {
    setSelectedIndex(index);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleNext = () => {
    setSelectedIndex((prev) => (prev + 1) % imageFiles.length);
  };

  const handlePrevious = () => {
    setSelectedIndex((prev) =>
      prev === 0 ? imageFiles.length - 1 : prev - 1
    );
  };

  const handleDownload = (imageUrl: string) => {
    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = imageUrl.split("/").pop() || "image";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Grid container spacing={1}>
        {imageFiles.map((image, index) => (
          <Grid item xs={12 / columns} key={index}>
            <Paper
              elevation={2}
              sx={{
                cursor: "pointer",
                overflow: "hidden",
                borderRadius: 1,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "scale(1.05)",
                  boxShadow: 4,
                },
              }}
              onClick={() => handleOpen(index)}
            >
              <img
                src={image}
                alt={`Image ${index + 1}`}
                style={{
                  width: maxWidth,
                  height: maxHeight,
                  objectFit: "cover",
                  display: "block",
                }}
              />
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Lightbox Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.95)",
          },
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "white",
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.2)",
            },
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>

        <IconButton
          onClick={() => handleDownload(imageFiles[selectedIndex])}
          sx={{
            position: "absolute",
            right: 56,
            top: 8,
            color: "white",
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.2)",
            },
            zIndex: 1,
          }}
        >
          <DownloadIcon />
        </IconButton>

        <DialogContent
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            padding: 0,
            minHeight: "70vh",
          }}
        >
          {imageFiles.length > 1 && (
            <>
              <IconButton
                onClick={handlePrevious}
                sx={{
                  position: "absolute",
                  left: 16,
                  color: "white",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                  },
                  zIndex: 1,
                }}
              >
                <NavigateBeforeIcon fontSize="large" />
              </IconButton>

              <IconButton
                onClick={handleNext}
                sx={{
                  position: "absolute",
                  right: 16,
                  color: "white",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                  },
                  zIndex: 1,
                }}
              >
                <NavigateNextIcon fontSize="large" />
              </IconButton>
            </>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
              padding: 2,
            }}
          >
            <img
              src={imageFiles[selectedIndex]}
              alt={`Image ${selectedIndex + 1}`}
              style={{
                maxWidth: "100%",
                maxHeight: "80vh",
                objectFit: "contain",
              }}
            />
          </Box>

          {imageFiles.length > 1 && (
            <Box
              sx={{
                position: "absolute",
                bottom: 16,
                left: "50%",
                transform: "translateX(-50%)",
                color: "white",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                padding: "4px 12px",
                borderRadius: 1,
                fontSize: "14px",
              }}
            >
              {selectedIndex + 1} / {imageFiles.length}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
